import {
  Box, Button, Flex, HStack, Icon, Text, Tooltip, VStack,
} from '@chakra-ui/react';
import { FiTrash2 } from 'react-icons/fi';
import { ILineItem } from '../../../lib/graphql';
import extractItemData from '../utils/extractItemData';
import formatMoney from '../../../utils/money';

// Item type tag component - moved outside to avoid re-creation on each render
const ItemTypeTag = ({ type, color }: { type: string; color: string }) => (
  <Text
    fontSize="xs"
    color={`${color}.600`}
    bg={`${color}.50`}
    px={2}
    py={1}
    borderRadius="md"
    fontWeight="medium"
  >
    {type}
  </Text>
);

interface BasketItemCardProps {
  lineItem: ILineItem;
  index: number;
  onRemoveItem: (itemId: number, itemType: string) => void;
  variant?: 'mobile' | 'desktop';
}

/**
 * Reusable basket item card component
 * Displays individual basket items with remove functionality
 * Supports both mobile and desktop layouts with different styling
 */
const BasketItemCard = ({
  lineItem,
  index,
  onRemoveItem,
  variant = 'mobile',
}: BasketItemCardProps): JSX.Element => {
  const itemData = extractItemData(lineItem);
  const isDesktop = variant === 'desktop';

  const getItemTypeTags = () => {
    const tags = [];
    if (lineItem.course) tags.push(<ItemTypeTag key="course" type="Course" color="blue" />);
    if (lineItem.exam) tags.push(<ItemTypeTag key="exam" type="Exam" color="green" />);
    if (lineItem.courseSession) tags.push(<ItemTypeTag key="session" type="Taster Class" color="purple" />);
    if (lineItem.eventTier) tags.push(<ItemTypeTag key="event" type="Event Ticket" color="orange" />);
    return tags;
  };

  if (isDesktop) {
    return (
      <Box
        key={`basketItem-${index}`}
        px={6}
        py={5}
        borderBottom="1px solid"
        borderColor="gray.100"
        _hover={{ bg: 'gray.50' }}
        transition="background-color 0.2s"
      >
        <Flex justify="space-between" align="flex-start" gap={4}>
          <VStack align="start" spacing={2} flex={1} minW={0}>
            <Tooltip label={itemData.name} placement="top-start" hasArrow>
              <Text
                fontSize="md"
                fontWeight="medium"
                color="gray.900"
                lineHeight="1.4"
                wordBreak="break-word"
                cursor="help"
              >
                {itemData.name}
              </Text>
            </Tooltip>
            <HStack spacing={3} wrap="wrap">
              {getItemTypeTags()}
            </HStack>
          </VStack>
          <HStack spacing={4} align="center" flexShrink={0}>
            <VStack spacing={0} align="end">
              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                {formatMoney(itemData.price)}
              </Text>
              <Text fontSize="xs" color="gray.500">
                per item
              </Text>
            </VStack>
            <Button
              onClick={() => onRemoveItem(itemData.id, itemData.type)}
              size="sm"
              variant="ghost"
              colorScheme="red"
              _hover={{ bg: 'red.50' }}
              flexShrink={0}
              title="Remove item"
            >
              <Icon as={FiTrash2} />
            </Button>
          </HStack>
        </Flex>
      </Box>
    );
  }

  // Mobile layout
  return (
    <Box
      key={`basketItem-${index}`}
      p={4}
      borderWidth={1}
      borderRadius="md"
      bg="gray.50"
    >
      <Flex justify="space-between" align="flex-start">
        <VStack align="start" spacing={1} flex={1}>
          <Text fontSize="sm" fontWeight="medium" lineHeight="short">
            {itemData.name}
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="blue.600">
            {formatMoney(itemData.price)}
          </Text>
        </VStack>
        <Button
          onClick={() => onRemoveItem(itemData.id, itemData.type)}
          size="sm"
          variant="ghost"
          colorScheme="red"
          ml={2}
        >
          <Icon as={FiTrash2} />
        </Button>
      </Flex>
    </Box>
  );
};

export default BasketItemCard;
